import { EventParam } from "@core/analytics/analytics-event";
import { analyticsService } from "@core/service-container";
import { AnalyticsCategory } from "@domain/analytics/analytics-types";
import {
	extractOnboardingTypeFromStep,
	OnboardingStep,
	OnboardingProgramQuickstartStep,
	onboardingProgramQuickstartSteps,
	OnboardingType,
} from "@domain/onboarding/onboarding";
import { Patient, PatientStatus, PendingPatient } from "@domain/patient/patient";
import { DatedPatientProgram, PatientProgram } from "@domain/program/patient-program";
import { NewProgram, Program, ProgramDetails } from "@domain/program/program";
import { TemplateItem } from "@domain/template/template";
import { TabPatientDetails } from "@ui/navigation/routes";
import { ScheduleProgramFormData } from "@ui/pages/program-builder/dialog/schedule-dialog/schedule-program-dialog";
import { isNotNil } from "@utils/assert";
import { computeDiffInDays, dateToDateDto } from "@utils/date-utils";
import _ from "lodash";

export enum AnalyticsProgramLocation {
	PROGRAM_BUILDER = "programBuilder",
	PATIENT_DETAIL = "patientDetail",
	HOME = "home",
	PROGRESS_TAB = "progressTab",
	PROGRAM_TAB = "programTab",
	PROGRAM_TAB_TOP = "programTabTop",
	PROGRAM_TAB_BOTTOM = "programTabBottom",
	PROGRAM_PAGE = "programPage",
}

export type AnalyticsExtendProgramLocation =
	| AnalyticsProgramLocation.HOME
	| AnalyticsProgramLocation.PROGRAM_TAB
	| AnalyticsProgramLocation.PROGRESS_TAB;

export type AnalyticsCreateProgramLocation =
	| AnalyticsProgramLocation.PROGRAM_PAGE
	| AnalyticsProgramLocation.PROGRESS_TAB
	| AnalyticsProgramLocation.PROGRAM_TAB_TOP
	| AnalyticsProgramLocation.PROGRAM_TAB_BOTTOM;

type AnalyticsDeleteProgramLocation =
	| AnalyticsProgramLocation.PATIENT_DETAIL
	| AnalyticsProgramLocation.PROGRAM_BUILDER;

type AnalyticsStopProgramLocation = AnalyticsProgramLocation.PATIENT_DETAIL | AnalyticsProgramLocation.PROGRAM_BUILDER;

export enum AnalyticsProgramClickType {
	LIST_CLICK = "listClick",
	OPTION_CLICK = "optionClick",
	PROGRAM_LIST_DETAIL = "programListDetail",
	PROGRAM_LIST_CLICK = "programListClick",
}

export const sendAnalyticsEventSaveProgram = (patient: Patient | PendingPatient, programId: string): void =>
	analyticsService.trackEvent({
		category: AnalyticsCategory.PROGRAM,
		action: "saveProgram",
		params: [patient.analyticsId, programId],
	});

export const sendAnalyticsEventCreateProgram = (
	location: AnalyticsCreateProgramLocation,
	analyticsId?: string,
	fromTemplate?: boolean,
): void =>
	analyticsService.trackEvent({
		category: AnalyticsCategory.PROGRAM,
		action: "createProgram",
		params: [location, analyticsId ?? "", fromTemplate ? "template" : "blank"],
	});

export const sendAnalyticsEventExtendProgram = (
	location: AnalyticsExtendProgramLocation,
	analyticsId: string,
	programId: string,
): void =>
	analyticsService.trackEvent({
		category: AnalyticsCategory.PROGRAM,
		action: "extendCTA",
		params: [analyticsId, programId, location],
	});

export const sendAnalyticsEventEditProgram = (params?: EventParam[]): void =>
	analyticsService.trackEvent({
		category: AnalyticsCategory.PROGRAM,
		action: "editProgram",
		params,
	});

export const sendAnalyticsEventDeleteProgram = (
	patient: { analyticsId: string },
	programId: string,
	location: AnalyticsDeleteProgramLocation,
): void =>
	analyticsService.trackEvent({
		category: AnalyticsCategory.PROGRAM,
		action: "deleteProgram",
		params: [location, patient.analyticsId, programId],
	});

export const sendAnalyticsEventStopProgram = (
	patient: { analyticsId: string },
	programId: string,
	location: AnalyticsStopProgramLocation,
): void =>
	analyticsService.trackEvent({
		category: AnalyticsCategory.PROGRAM,
		action: "stopProgram",
		params: [location, patient.analyticsId, programId],
	});

export const sendAnalyticsEventViewPatientDetail = (analyticsId: string, clickType: AnalyticsProgramClickType): void =>
	analyticsService.trackEvent({
		category: AnalyticsCategory.PROGRAM,
		action: "viewPatientDetail",
		params: [TabPatientDetails.PROGRESS_TAB, analyticsId, clickType],
	});

export const sendAnalyticsEventSwitchPatientDetail = (analyticsId: string, tab: TabPatientDetails): void =>
	analyticsService.trackEvent({
		category: AnalyticsCategory.PROGRAM,
		action: "switchPatientDetail",
		params: [tab, analyticsId],
	});

export const sendAnalyticsEventDateModification = (
	patientId: string,
	program: Program | NewProgram,
	dates: ScheduleProgramFormData,
): void =>
	analyticsService.trackEvent({
		category: AnalyticsCategory.PROGRAM,
		action: "dateModification",
		params: _.compact([
			patientId,
			_.get(program, "id"),
			isNotNil(dates.startDate) && dateToDateDto(dates.startDate),
			dates.endDate && dateToDateDto(dates.endDate),
		]),
	});

export const sendAnalyticsEventUseTemplateForProgram = (
	analyticsId: string,
	template: TemplateItem,
	exerciseDetails: ProgramDetails,
	patientStatus?: PatientStatus,
): void =>
	analyticsService.trackEvent({
		category: AnalyticsCategory.PROGRAM,
		action: "useTemplateForProgram",
		params: [
			analyticsId,
			template.id,
			template.sessionsPerWeek,
			template.exercisesPerWeek,
			exerciseDetails.exerciseTitles.join(";"),
			exerciseDetails.exerciseUnits.join(";"),
			exerciseDetails.exerciseSets.join(";"),
			patientStatus ?? "",
			exerciseDetails.exerciseIds.join(";"),
			exerciseDetails.exerciseRevisions.join(";"),
			exerciseDetails.exerciseCategories.join(";"),
			exerciseDetails.exerciseBodyParts.join(";"),
			exerciseDetails.exerciseDifficulties.join(";"),
			exerciseDetails.exerciseStartingPosition.join(";"),
		],
	});

export const sendAnalyticsEventStartProgram = (
	analyticsId: string,
	startedProgram: DatedPatientProgram,
	exerciseDetails: ProgramDetails,
	patientStatus?: PatientStatus,
): void =>
	analyticsService.trackEvent({
		category: AnalyticsCategory.PROGRAM,
		action: "startProgram",
		params: [
			analyticsId,
			startedProgram.id,
			startedProgram.sessionsPerWeek,
			startedProgram.exercisesPerWeek,
			computeDiffInDays(startedProgram.startDate, startedProgram.endDate),
			dateToDateDto(startedProgram.startDate),
			exerciseDetails.exerciseUnits.join(";"),
			exerciseDetails.exerciseSets.join(";"),
			patientStatus === PatientStatus.Pending ? "pending" : "registered",
			exerciseDetails.exerciseIds.join(";"),
			exerciseDetails.exerciseRevisions.join(";"),
			exerciseDetails.exerciseCategories.join(";"),
			exerciseDetails.exerciseBodyParts.join(";"),
			exerciseDetails.exerciseDifficulties.join(";"),
			exerciseDetails.exerciseStartingPosition.join(";"),
		],
	});

export const sendAnalyticsEventViewProgram = (
	analyticsId: string,
	program: PatientProgram,
	action: AnalyticsProgramClickType | TabPatientDetails,
): void =>
	analyticsService.trackEvent({
		category: AnalyticsCategory.PROGRAM,
		action: "viewProgram",
		params: [analyticsId, program.id, program.status.toLowerCase(), action],
	});

export const sendAnalyticsEventOpenMoodTooltip = (): void =>
	analyticsService.trackEvent({
		category: AnalyticsCategory.PROGRAM,
		action: "openMoodTooltip",
	});

export const sendAnalyticsEventOpenExertionTooltip = (): void =>
	analyticsService.trackEvent({
		category: AnalyticsCategory.PROGRAM,
		action: "openExertionTooltip",
	});

export const sendAnalyticsEventDuplicateProgramAsDraft = (programId: string): void =>
	analyticsService.trackEvent({
		category: AnalyticsCategory.PROGRAM,
		action: "duplicateProgramAsDraft",
		params: [programId],
	});

export const sendAnalyticsEventDuplicateProgramAsTemplate = (programId: string): void =>
	analyticsService.trackEvent({
		category: AnalyticsCategory.PROGRAM,
		action: "duplicateProgramAsTemplate",
		params: [programId],
	});

export const sendAnalyticsClearAllFilters = (): void =>
	analyticsService.trackEvent({
		category: AnalyticsCategory.PROGRAM,
		action: "clearAll",
		params: [],
	});

export const sendAnalyticsEventProgramDurationExtendedShortcut = (value: number): void =>
	analyticsService.trackEvent({
		category: AnalyticsCategory.PROGRAM,
		action: "endDateExtendedShortcut",
		params: [value],
	});

export const sendAnalyticsEventSetProgramDurationShortcut = (value: number): void =>
	analyticsService.trackEvent({
		category: AnalyticsCategory.PROGRAM,
		action: "endDateShortcut",
		params: [value],
	});

export const sendAnalyticsEventViewGuide = (guideStep: OnboardingStep): void => {
	const onboardingType = extractOnboardingTypeFromStep(guideStep);
	const stepsOrder = onboardingType === OnboardingType.PROGRAM_QUICKSTART ? onboardingProgramQuickstartSteps : [];
	const currentStepIndex = stepsOrder.indexOf(guideStep as OnboardingProgramQuickstartStep);
	analyticsService.trackEvent({
		category: AnalyticsCategory.PROGRAM,
		action: "viewGuide",
		params: [getAnalyticsLabel(onboardingType), currentStepIndex + 1],
	});
};

const onboardingTypeLabels: Record<OnboardingType, string> = {
	[OnboardingType.GET_STARTED]: "getStarted",
	[OnboardingType.PROGRAM_QUICKSTART]: "programQuickstart",
};

const getAnalyticsLabel = (guideType: OnboardingType): string => onboardingTypeLabels[guideType];
